import { Box, debounce, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import PaginatedTable from "Components/shared/PaginatedTable";
import CustomButton from "Components/shared/Button";
import { useRTL } from "hooks/isRTL";
import { orderStatus } from "enums/shared";
import formatDateForInput from "utils/formatDateForInput";
import formatToUTC from "utils/formatToUTC";
import { ORDER_FILTERS } from "constants/paginationConstants";
import { fallbackValue } from "constants/fallbackConstant";

const OrdersTabShared = ({
  id,
  queryKey,
  fetchFn,
  ClearFilterButton = false,
  columns,
  initialStatus = null,
  navigateToOrderDetails,
  showEmptyStateMessage = false,
}) => {
  const { t } = useRTL();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [filters, setFilters] = useState({
    ...ORDER_FILTERS,
    status: initialStatus,
  });

  const fetchOrders = async () => {
    const {
      status,
      senderName,
      receiverName,
      orderNumber,
      pickupDate,
      dropoffDate,
      pickUpLocation,
      dropOffLocation,
    } = filters;

    const validPickupDate = pickupDate ? formatToUTC(pickupDate) : null;
    const validDropoffDate = dropoffDate ? formatToUTC(dropoffDate) : null;

    return fetchFn(
      id,
      rowsPerPage,
      page + 1,
      status !== null ? Number(status) : null,
      senderName || null,
      receiverName || null,
      orderNumber || null,
      validPickupDate,
      validDropoffDate,
      pickUpLocation || null,
      dropOffLocation || null
    );
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      queryKey,
      { id, page: page + 1, pageSize: rowsPerPage, ...filters },
    ],
    queryFn: fetchOrders,
    enabled: !!id,
    select: (data) => ({
      rows: data.page.map((order) => ({
        ...order,
        status: t(orderStatus[order.status]?.value || "Unknown"),
        pickupDateTime: order.prefeeredPickUpDate
          ? formatDateForInput(order.prefeeredPickUpDate)
          : fallbackValue,
        dropOffDateTime: order.prefeeredDeliveryDate
          ? formatDateForInput(order.prefeeredDeliveryDate)
          : fallbackValue,
      })),
      totalRecords: data.totalRecords || 0,
    }),
    keepPreviousData: true,
  });

  const statusOptions = Object.entries(orderStatus).map(([key, value]) => ({
    value: Number(key),
    label: t(value.value),
  }));

  const applyFiltersImmediately = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
  };

  const debouncedUpdateFilters = useMemo(
    () =>
      debounce((newFilters) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
        setPage(0);
      }, 500),
    []
  );

  const setDebouncedFilters = useCallback(
    (newFilters) => {
      debouncedUpdateFilters(newFilters);
    },
    [debouncedUpdateFilters]
  );

  const applyFiltersWithDebounce = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setDebouncedFilters(updatedFilters);
  };

  const handleSearch = (value, columnId) => {
    if (
      [
        "orderNumber",
        "senderName",
        "receiverName",
        "pickUpLocation",
        "dropOffLocation",
      ].includes(columnId)
    ) {
      applyFiltersWithDebounce({ [columnId]: value });
    }
  };

  const handleDateChange = (key, date) => {
    const updatedFilters = { ...filters, [key]: date };
    setFilters(updatedFilters);
  };

  const handleStatusChange = (value) => {
    applyFiltersImmediately({ status: value });
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      status: null,
      senderName: "",
      orderNumber: null,
      receiverName: "",
      pickupDate: null,
      dropoffDate: null,
      pickUpLocation: "",
      dropOffLocation: "",
    };
    setFilters(clearedFilters);
    setDebouncedFilters(clearedFilters);
  };

  if (isError) return <p>{t("Failed to load orders.")}</p>;

  // Show empty state if no orders and showEmptyStateMessage is true
  if (showEmptyStateMessage && !isLoading && data?.totalRecords === 0) {
    return (
      <Box sx={{ padding: "2rem", textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary">
          No orders in route
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "1rem" }}>
      {ClearFilterButton && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <CustomButton onClick={handleClearFilters}>
            {t("clearFilters")}
          </CustomButton>
        </Box>
      )}

      <PaginatedTable
        isLoading={isLoading}
        columns={columns}
        rows={data?.rows || []}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRecords={data?.totalRecords || 0}
        handleChangePage={(event, newPage) => setPage(newPage)}
        handleChangeRowsPerPage={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        showActions={false}
        onRowClick={(row) => navigateToOrderDetails(row.id)}
        filters={filters}
        onSearch={handleSearch}
        handleDateChange={handleDateChange}
        ordrStatusOptions={statusOptions}
        handleStatusChange={handleStatusChange}
      />
    </Box>
  );
};

export default OrdersTabShared;
